// Visual themes for campaign offers, keyed by the API's `display.theme`.
//
// This map is the only place a specific festival is named. Every other offer
// component renders whatever the API sends, so a new campaign (New Year,
// Holi, ...) works without an app release: an unknown theme falls back to the
// generic style built from the API's own colours and banner image.

const DIWALI_FONT_HREF = "https://fonts.googleapis.com/css2?family=Yatra+One&display=swap";

const OFFER_THEMES = {
  diwali: {
    secondary: "#4A0E2E",
    primary: "#7A1F3D",
    accent: "#F5B700",
    accentSoft: "#FFD36E",
    text: "#FFF8EC",
    icon: "🪔",
    artwork: "diwali",
    headingFont: "'Yatra One', Georgia, serif",
    fontHref: DIWALI_FONT_HREF,
    festivalName: "Diwali",
    greeting: "Happy Diwali!",
  },
};

const GENERIC_THEME = {
  secondary: "",
  primary: "#503691",
  accent: "#F5B700",
  accentSoft: "#FFD36E",
  text: "",
  icon: "🎁",
  artwork: null,
  headingFont: "",
  fontHref: "",
  festivalName: "",
  greeting: "Offer unlocked!",
};

const LIGHT_TEXT = "#FFFFFF";
const DARK_TEXT = "#1F1235";
const MIN_CONTRAST = 4.5;

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeHex = (value) => {
  const raw = String(value || "").trim();
  if (!HEX_COLOR.test(raw)) return "";
  if (raw.length === 4) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toUpperCase();
  }
  return raw.toUpperCase();
};

const hexToRgb = (hex) => {
  const value = normalizeHex(hex).slice(1);
  return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
};

const relativeLuminance = (hex) => {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (foreground, background) => {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// Darkens a colour by mixing it toward black, for the deep end of a gradient.
const shade = (hex, amount) => {
  const [r, g, b] = hexToRgb(hex).map((channel) => Math.round(channel * (1 - amount)));
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
};

// Lowest contrast a text colour gets against every stop of the background.
const worstContrast = (text, backgrounds) =>
  Math.min(...backgrounds.map((background) => contrastRatio(text, background)));

// Keeps the requested text colour when it is readable on every background
// stop, otherwise picks whichever of light/dark text reads better.
export const pickReadableText = (backgrounds, preferred = "") => {
  const stops = (Array.isArray(backgrounds) ? backgrounds : [backgrounds]).filter(Boolean);
  if (stops.length === 0) return preferred || DARK_TEXT;
  if (preferred && worstContrast(preferred, stops) >= MIN_CONTRAST) return preferred;
  return worstContrast(LIGHT_TEXT, stops) >= worstContrast(DARK_TEXT, stops) ? LIGHT_TEXT : DARK_TEXT;
};

const toRgba = (hex, alpha) => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Turns an offer's `display` block into concrete colours, fonts and artwork.
 * Every API colour is optional: invalid or missing values fall back to the
 * theme defaults, and text colours are corrected when they would not meet
 * WCAG AA contrast on the banner background.
 */
export const resolveOfferTheme = (display = {}) => {
  const key = String(display?.theme || "").trim().toLowerCase();
  const base = OFFER_THEMES[key] || GENERIC_THEME;

  const primary = normalizeHex(display?.primaryColor) || base.primary;
  const secondary = normalizeHex(display?.secondaryColor) || base.secondary || shade(primary, 0.35);
  const accent = normalizeHex(display?.accentColor) || base.accent;
  const accentSoft = base.accentSoft || accent;
  const text = pickReadableText([secondary, primary], normalizeHex(display?.textColor) || base.text);
  const onAccent = pickReadableText([accent, accentSoft], secondary);

  return {
    key: OFFER_THEMES[key] ? key : "generic",
    primary,
    secondary,
    accent,
    accentSoft,
    text,
    mutedText: toRgba(text, 0.88),
    onAccent,
    background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,
    badgeBackground: `linear-gradient(135deg, ${accent} 0%, ${accentSoft} 100%)`,
    bannerImageUrl: String(display?.bannerImageUrl || "").trim(),
    iconUrl: String(display?.iconUrl || "").trim(),
    icon: base.icon,
    artwork: base.artwork,
    headingFont: base.headingFont,
    fontHref: base.fontHref,
    festivalName: base.festivalName,
    greeting: base.greeting,
  };
};

// Copy that depends on the festival, so components never spell one out.
export const getOfferLabels = (theme) => {
  const bonusLabel = theme?.festivalName ? `${theme.festivalName} bonus` : "Offer bonus";
  return {
    bonusLabel,
    bestValue: theme?.festivalName ? `Best ${theme.festivalName} value` : "Best value",
    noStackNote: `${bonusLabel} can't be combined with promo codes`,
    greeting: theme?.greeting || GENERIC_THEME.greeting,
  };
};

const loadedFonts = new Set();

// Festive heading fonts are fetched only once an offer actually renders, so the
// app downloads nothing extra while no campaign is live.
export const ensureOfferFont = (theme) => {
  const href = theme?.fontHref;
  if (!href || loadedFonts.has(href) || typeof document === "undefined") return;
  loadedFonts.add(href);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

export const formatMonths = (count) => {
  const months = Number(count) || 0;
  return `${months} month${months === 1 ? "" : "s"}`;
};

export const formatBonusMonths = (count) => {
  const months = Number(count) || 0;
  return `${months} bonus month${months === 1 ? "" : "s"}`;
};

export const formatOfferEndDate = (value) => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return `${date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })} IST`;
};
